import { questions } from "../data/content";
import type { DimensionId, Level, ResultCode, ScoringResult } from "../types";

const levelNumber: Record<Level, number> = { L: 0, M: 1, H: 2 };
const dimensionWeights: Record<DimensionId, number> = { D1: 1, D2: 1, D3: 1, D4: 1.5 };
const maximumDistance = Object.values(dimensionWeights).reduce((sum, weight) => sum + weight * 2, 0);

const templates: Array<{ code: Exclude<ResultCode, "MIXD">; vector: Record<DimensionId, Level>; priority: number }> = [
  { code: "COOL", vector: { D1: "H", D2: "H", D3: "M", D4: "L" }, priority: 1 },
  { code: "GIVE", vector: { D1: "H", D2: "L", D3: "M", D4: "M" }, priority: 2 },
  { code: "ONER", vector: { D1: "H", D2: "L", D3: "L", D4: "H" }, priority: 3 },
  { code: "NITE", vector: { D1: "M", D2: "M", D3: "L", D4: "H" }, priority: 4 },
  { code: "HARD", vector: { D1: "H", D2: "M", D3: "L", D4: "M" }, priority: 5 },
  { code: "LEAN", vector: { D1: "H", D2: "H", D3: "H", D4: "L" }, priority: 6 },
  { code: "FULL", vector: { D1: "M", D2: "L", D3: "L", D4: "H" }, priority: 7 },
  { code: "QUIT", vector: { D1: "M", D2: "H", D3: "H", D4: "L" }, priority: 8 },
];

export function scoreProfile(profile: Record<DimensionId, Level>) {
  return templates
    .map((template) => {
      const dimensions = Object.keys(profile) as DimensionId[];
      const distance = dimensions.reduce(
        (sum, dimension) =>
          sum + Math.abs(levelNumber[profile[dimension]] - levelNumber[template.vector[dimension]]) * dimensionWeights[dimension],
        0,
      );
      const coreMatches = dimensions.filter((dimension) => profile[dimension] === template.vector[dimension]).length;
      return { ...template, distance, coreMatches, similarity: 1 - distance / maximumDistance };
    })
    .sort((a, b) => a.distance - b.distance || a.priority - b.priority || b.coreMatches - a.coreMatches || a.code.localeCompare(b.code));
}

export function toLevel(score: number): Level {
  if (score <= 1.5) return "L";
  if (score < 2.5) return "M";
  return "H";
}

export function calculateResult(answers: Record<string, string>): ScoringResult {
  if (questions.some((question) => !answers[question.id])) {
    throw new Error("ANSWER_INCOMPLETE");
  }

  const rawSums: Record<DimensionId, number> = { D1: 0, D2: 0, D3: 0, D4: 0 };
  const questionCounts: Record<DimensionId, number> = { D1: 0, D2: 0, D3: 0, D4: 0 };
  const tags = new Set<string>();

  for (const question of questions) {
    const option = question.options.find((item) => item.id === answers[question.id]);
    if (!option) throw new Error("INVALID_OPTION");
    rawSums[question.dimension] += option.score;
    questionCounts[question.dimension] += 1;
    option.tags?.forEach((tag) => tags.add(tag));
  }

  const dimensions = ["D1", "D2", "D3", "D4"] as DimensionId[];
  if (dimensions.some((dimension) => questionCounts[dimension] === 0)) {
    throw new Error("DIMENSION_WITHOUT_QUESTION");
  }
  const dimensionScores = Object.fromEntries(
    dimensions.map((dimension) => [dimension, rawSums[dimension] / questionCounts[dimension]]),
  ) as Record<DimensionId, number>;

  const profile: Record<DimensionId, Level> = {
    D1: toLevel(dimensionScores.D1),
    D2: toLevel(dimensionScores.D2),
    D3: toLevel(dimensionScores.D3),
    D4: toLevel(dimensionScores.D4),
  };
  const ranked = scoreProfile(profile);

  if (ranked[0].similarity < 0.6) {
    return { resultCode: "MIXD", similarity: ranked[0].similarity, profile, dimensionScores, behaviorTags: [...tags] };
  }

  const secondaryCode = ranked[0].similarity - ranked[1].similarity <= 1 / maximumDistance ? ranked[1].code : undefined;
  return {
    resultCode: ranked[0].code,
    similarity: ranked[0].similarity,
    secondaryCode,
    profile,
    dimensionScores,
    behaviorTags: [...tags],
  };
}
